import React, { useState, useEffect, useRef, useMemo } from 'react';
import ProtectedRoute from '../../routes/protected';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle';
import careerService from '../../services/data/career.service';
import companyService from '../../services/data/company.service';
import useUserData from '../../context/user';
import useCompanyData from '../../context/company';
import UsePerms from '../../context/userAccess';
import CareerHeader from './careerHeader';
import CareerMenu from './careerMenu';
import SkeletonSummary from './skeletonSummary';

const REASON_OPTIONS = [
    { value: 'TR', label: 'Transferência' },
    { value: 'PM', label: 'Promoção' },
    { value: 'PC', label: 'Mudança de Cargo' },
    { value: 'AJ', label: 'Ajuste' },
];

const STATUS_BADGE = {
    DR: { label: 'Rascunho',                  cls: 'bg-secondary' },
    PC: { label: 'Aguard. Gestor Atual',       cls: 'bg-warning text-dark' },
    PR: { label: 'Aguard. Gestor Destino',     cls: 'bg-info text-dark' },
    PF: { label: 'Aguard. Aprovação Final',    cls: 'bg-primary' },
    AP: { label: 'Aprovado',                   cls: 'bg-success' },
    RJ: { label: 'Rejeitado',                  cls: 'bg-danger' },
    CN: { label: 'Cancelado',                  cls: 'bg-secondary' },
};

function StatusBadge({ status }) {
    const s = STATUS_BADGE[status] || { label: status, cls: 'bg-secondary' };
    return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-BR');
}

export default function CareerTransfer() {
    const { id } = useParams();
    const { user } = useUserData();
    const { rootUser } = useCompanyData();
    const VerifyPerms = UsePerms();
    const toast = useRef(null);
    const offCanvasInitRef = useRef();
    const offCanvasDetailRef = useRef();
    const offCanvasConfigureRef = useRef();

    const [pageLoad, setPageLoad] = useState(true);
    const [userEmployee, setUserEmployee] = useState('');
    const [myEmployee, setMyEmployee] = useState(null);
    const [showPerformanceTabs, setShowPerformanceTabs] = useState(false);
    const [transfers, setTransfers] = useState([]);
    const [transfersNext, setTransfersNext] = useState(null);
    const [transfersPage, setTransfersPage] = useState(1);
    const [transfersLoad, setTransfersLoad] = useState(false);
    const [activeTransfer, setActiveTransfer] = useState(null);
    const [selectedTransfer, setSelectedTransfer] = useState(null);

    // form state
    const [formReason, setFormReason] = useState('TR');
    const [formDate, setFormDate] = useState('');
    const [formNotes, setFormNotes] = useState('');
    const [formSubmitting, setFormSubmitting] = useState(false);

    // review form
    const [reviewNotes, setReviewNotes] = useState('');
    const [reviewSubmitting, setReviewSubmitting] = useState(false);

    // configure form
    const [jobSearch, setJobSearch] = useState('');
    const [jobResults, setJobResults] = useState([]);
    const [jobSearching, setJobSearching] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [configSubmitting, setConfigSubmitting] = useState(false);

    // superiors for init form
    const [superiorOptions, setSuperiorOptions] = useState([]);
    const [superiorsLoading, setSuperiorsLoading] = useState(false);
    const [selectedSuperior, setSelectedSuperior] = useState(null);

    // final approval
    const [finalSubmitting, setFinalSubmitting] = useState(false);

    const companyId = localStorage.getItem('companyId');

    useEffect(() => { loadEmployee(); }, []);

    function loadEmployee() {
        const data = { company_id: companyId, id };
        careerService.getEmployeeSummary(data)
            .then(r => setUserEmployee(r))
            .catch(err => console.log(err))
            .finally(() => setPageLoad(false));
    }

    useEffect(() => {
        if (!user || !companyId) return;
        careerService.getEmployeeSummary({ company_id: companyId })
            .then(r => setMyEmployee(r))
            .catch(() => {});
    }, [user]);

    useEffect(() => {
        if (!userEmployee) return;
        if (userEmployee.manager && userEmployee.manager.uid === user.uid) {
            setShowPerformanceTabs(true);
            return;
        }
        if (userEmployee.user?.id === user.uid) {
            setShowPerformanceTabs(true);
            return;
        }
        if (rootUser) {
            setShowPerformanceTabs(true);
        }
    }, [userEmployee, user, rootUser]);

    useEffect(() => {
        if (id) loadTransfers(1);
    }, [id]);

    function loadTransfers(page) {
        setTransfersLoad(true);
        careerService.listTransfers(page, { employee_id: id })
            .then(data => {
                setTransfers(prev => page === 1 ? data.results : [...prev, ...data.results]);
                setTransfersNext(data.next);
                setTransfersPage(page);
                const pending = data.results.find(t => ['DR', 'PC', 'PR', 'PF'].includes(t.status));
                if (page === 1 && pending) setActiveTransfer(pending);
            })
            .catch(() => toast.current?.show({ severity: 'warn', summary: 'Erro', detail: 'Erro ao carregar movimentações', life: 3000 }))
            .finally(() => setTransfersLoad(false));
    }

    function openCanvas(ref) {
        new bootstrap.Offcanvas(ref.current).show();
    }

    function closeCanvas(ref) {
        bootstrap.Offcanvas.getInstance(ref.current)?.hide();
    }

    async function handleSubmitTransfer(e) {
        e.preventDefault();
        if (!formDate || !selectedJob) return;
        if (superiorOptions.length > 0 && !selectedSuperior) return;
        setFormSubmitting(true);
        try {
            await careerService.createTransfer({
                company_id: companyId,
                employee_id: id,
                reason: formReason,
                effective_date: formDate,
                notes: formNotes,
                future_job_position_id: selectedJob.id,
                receiving_manager_id: selectedSuperior?.id || null,
            });
            toast.current.show({ severity: 'success', summary: 'Movimentação iniciada!', detail: 'Gestor atual foi notificado.', life: 4000 });
            closeCanvas(offCanvasInitRef);
            setFormReason('TR'); setFormDate(''); setFormNotes('');
            setSelectedJob(null); setJobSearch(''); setJobResults([]);
            setSuperiorOptions([]); setSelectedSuperior(null);
            loadTransfers(1);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.response?.data?.[0] || 'Erro ao criar movimentação';
            toast.current.show({ severity: 'error', summary: 'Erro', detail: msg, life: 4000 });
        } finally {
            setFormSubmitting(false);
        }
    }

    async function handleReview(action) {
        setReviewSubmitting(true);
        try {
            await careerService.transferCurrentManagerReview({
                id: selectedTransfer.id,
                action,
                notes: reviewNotes,
            });
            const label = action === 'approve' ? 'Movimentação aprovada!' : 'Movimentação rejeitada.';
            toast.current.show({ severity: action === 'approve' ? 'success' : 'info', summary: label, life: 4000 });
            closeCanvas(offCanvasDetailRef);
            setReviewNotes('');
            loadTransfers(1);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Erro ao revisar movimentação';
            toast.current.show({ severity: 'error', summary: 'Erro', detail: msg, life: 4000 });
        } finally {
            setReviewSubmitting(false);
        }
    }

    async function handleConfigure(e) {
        e.preventDefault();
        if (!selectedJob) return;
        setConfigSubmitting(true);
        try {
            await careerService.transferReceivingManagerConfigure({
                id: selectedTransfer.id,
                future_job_position_id: selectedJob.id,
            });
            toast.current.show({ severity: 'success', summary: 'Configuração salva!', detail: 'Aguardando aprovação final.', life: 4000 });
            closeCanvas(offCanvasConfigureRef);
            setSelectedJob(null); setJobSearch(''); setJobResults([]);
            loadTransfers(1);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Erro ao configurar';
            toast.current.show({ severity: 'error', summary: 'Erro', detail: msg, life: 4000 });
        } finally {
            setConfigSubmitting(false);
        }
    }

    async function handleFinalApproval(action) {
        setFinalSubmitting(true);
        try {
            await careerService.transferFinalApproval({ id: selectedTransfer.id, action });
            const label = action === 'approve' ? 'Movimentação aprovada e efetivada!' : 'Movimentação rejeitada.';
            toast.current.show({ severity: action === 'approve' ? 'success' : 'info', summary: label, life: 4000 });
            closeCanvas(offCanvasDetailRef);
            loadTransfers(1);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Erro na aprovação final';
            toast.current.show({ severity: 'error', summary: 'Erro', detail: msg, life: 4000 });
        } finally {
            setFinalSubmitting(false);
        }
    }

    async function handleCancel(transferId) {
        if (!window.confirm('Cancelar movimentação?')) return;
        try {
            await careerService.cancelTransfer({ id: transferId });
            toast.current.show({ severity: 'info', summary: 'Cancelado', life: 3000 });
            loadTransfers(1);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro ao cancelar', life: 3000 });
        }
    }

    async function searchJobs(val) {
        setJobSearch(val);
        if (val.length < 2) { setJobResults([]); return; }
        setJobSearching(true);
        try {
            const data = await companyService.autoCompleteJobPosition(val);
            setJobResults(data || []);
        } catch {
            setJobResults([]);
        } finally {
            setJobSearching(false);
        }
    }

    async function loadSuperiors(jobPositionId) {
        setSuperiorOptions([]);
        setSelectedSuperior(null);
        setSuperiorsLoading(true);
        try {
            const data = await careerService.getJobPositionSuperiors({
                company_id: companyId,
                job_position_id: jobPositionId,
            });
            setSuperiorOptions(data || []);
            if (data && data.length === 1) setSelectedSuperior(data[0]);
        } catch {
            setSuperiorOptions([]);
        } finally {
            setSuperiorsLoading(false);
        }
    }

    function openDetailCanvas(transfer) {
        setSelectedTransfer(transfer);
        setReviewNotes('');
        setSelectedJob(null);
        setJobSearch('');
        setJobResults([]);
        openCanvas(offCanvasDetailRef);
    }

    function openConfigureCanvas(transfer) {
        setSelectedTransfer(transfer);
        setSelectedJob(transfer.future_job_position || null);
        setJobSearch('');
        setJobResults([]);
        openCanvas(offCanvasConfigureRef);
    }

    const isOwnProfile = userEmployee?.user?.id === user?.uid;
    const isAnyManager = (myEmployee?.team?.length > 0) || rootUser;
    const isCurrentManager = userEmployee?.manager && userEmployee.manager.uid === user?.uid;
    const isReceivingManager = activeTransfer && myEmployee && activeTransfer.receiving_manager?.id === myEmployee.id;
    const isFinalApprover = activeTransfer && myEmployee && activeTransfer.final_approver?.id === myEmployee.id;
    const canInitiate = isAnyManager && !isOwnProfile && !activeTransfer;
    const showTransferTab = isOwnProfile || isAnyManager;

    const hasApprovedTransfer = useMemo(
        () => (transfers || []).some(t => t.status === 'AP'),
        [transfers]
    );
    const showSalaryHint =
        hasApprovedTransfer &&
        userEmployee?.id &&
        (isOwnProfile || VerifyPerms(['change_jobposition']) || rootUser);

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />

            <div className="container-fluid">
                <CareerHeader Employee={userEmployee} />

                <div className="my-4 mx-5 py-3 rounded-top" style={{ minHeight: '500px' }}>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0">
                        <CareerMenu EmployeeId={userEmployee.id} showPerformanceTabs={showPerformanceTabs} showTransferTab={showTransferTab} Page="transfer" />
                    </div>

                    <div className="bg-white py-4 rounded formMarginBottom">
                        {pageLoad ? <SkeletonSummary /> : (
                            <div className="px-4">

                                {/* Header + ação */}
                                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                    <div>
                                        <h5 className="fw-bold mb-0">
                                            <FontAwesomeIcon icon={solid('arrow-right-arrow-left')} className="me-2 text-green" />
                                            Movimentação de Cargo
                                        </h5>
                                        <p className="text-muted small mb-0">Histórico e status das movimentações do colaborador</p>
                                    </div>
                                    {canInitiate && (
                                        <button className="btn btn-success btn-sm btn-rounded px-3" onClick={() => { setSelectedJob(null); setJobSearch(''); setJobResults([]); setSuperiorOptions([]); setSelectedSuperior(null); openCanvas(offCanvasInitRef); }}>
                                            <FontAwesomeIcon icon={solid('plus')} className="me-1" />
                                            Iniciar Movimentação
                                        </button>
                                    )}
                                </div>

                                {showSalaryHint && (
                                    <div className="alert alert-light border border-success small mb-4" role="status">
                                        <FontAwesomeIcon icon={solid('coins')} className="me-2 text-success" />
                                        Há movimentação efetivada neste perfil. Registre o salário vigente para permitir análise de enquadramento na faixa da nova posição em{' '}
                                        <Link to={`/cargos-salarios/registrar-salarios/?employee=${userEmployee.id}`} className="fw-semibold text-success text-decoration-none">
                                            Cargos &amp; Salários — Registrar salários
                                        </Link>
                                        .
                                    </div>
                                )}

                                {/* Movimentação ativa */}
                                {activeTransfer && (
                                    <div className="card border-0 shadow-sm mb-4">
                                        <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                                            <span className="fw-semibold text-muted">
                                                <FontAwesomeIcon icon={solid('clock-rotate-left')} className="me-2 text-warning" />
                                                Movimentação em andamento
                                            </span>
                                            <StatusBadge status={activeTransfer.status} />
                                        </div>
                                        <div className="card-body">
                                            <div className="row g-3">
                                                <div className="col-sm-4">
                                                    <p className="small text-muted mb-0">Motivo</p>
                                                    <p className="fw-semibold mb-0">{activeTransfer.reason_display}</p>
                                                </div>
                                                <div className="col-sm-4">
                                                    <p className="small text-muted mb-0">Data efetiva</p>
                                                    <p className="fw-semibold mb-0">{formatDate(activeTransfer.effective_date)}</p>
                                                </div>
                                                <div className="col-sm-4">
                                                    <p className="small text-muted mb-0">Gestor de destino</p>
                                                    <p className="fw-semibold mb-0">{activeTransfer.receiving_manager?.name || '—'}</p>
                                                </div>
                                                {activeTransfer.future_job_position && (
                                                    <div className="col-sm-6">
                                                        <p className="small text-muted mb-0">Posição de destino</p>
                                                        <p className="fw-semibold mb-0">{activeTransfer.future_job_position.title}</p>
                                                        <p className="small text-muted mb-0">{activeTransfer.future_job_position.subcompany?.title} · {activeTransfer.future_job_position.department?.title}</p>
                                                    </div>
                                                )}
                                                {activeTransfer.notes && (
                                                    <div className="col-12">
                                                        <p className="small text-muted mb-0">Observações</p>
                                                        <p className="mb-0">{activeTransfer.notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-3 d-flex gap-2 flex-wrap">
                                                <button className="btn btn-sm btn-outline-secondary btn-rounded" onClick={() => openDetailCanvas(activeTransfer)}>
                                                    <FontAwesomeIcon icon={solid('eye')} className="me-1" />
                                                    Ver detalhes
                                                </button>
                                                {/* Gestor atual revisa */}
                                                {isCurrentManager && activeTransfer.status === 'PC' && (
                                                    <button className="btn btn-sm btn-warning btn-rounded" onClick={() => openDetailCanvas(activeTransfer)}>
                                                        <FontAwesomeIcon icon={solid('check')} className="me-1" />
                                                        Revisar
                                                    </button>
                                                )}
                                                {/* Gestor destino configura */}
                                                {isReceivingManager && activeTransfer.status === 'PR' && (
                                                    <button className="btn btn-sm btn-info btn-rounded" onClick={() => openConfigureCanvas(activeTransfer)}>
                                                        <FontAwesomeIcon icon={solid('sliders')} className="me-1" />
                                                        Confirmar Movimentação
                                                    </button>
                                                )}
                                                {/* Aprovador final */}
                                                {isFinalApprover && activeTransfer.status === 'PF' && (
                                                    <button className="btn btn-sm btn-primary btn-rounded" onClick={() => openDetailCanvas(activeTransfer)}>
                                                        <FontAwesomeIcon icon={solid('stamp')} className="me-1" />
                                                        Aprovar / Rejeitar
                                                    </button>
                                                )}
                                                {/* Cancelar */}
                                                {(isReceivingManager || rootUser) && ['PC', 'PR', 'PF'].includes(activeTransfer.status) && (
                                                    <button className="btn btn-sm btn-outline-danger btn-rounded" onClick={() => handleCancel(activeTransfer.id)}>
                                                        <FontAwesomeIcon icon={solid('xmark')} className="me-1" />
                                                        Cancelar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Fluxo de etapas */}
                                {activeTransfer && (
                                    <div className="card border-0 shadow-sm mb-4">
                                        <div className="card-body py-3">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                {[
                                                    { code: 'PC', label: '1. Revisão Gestor Atual' },
                                                    { code: 'PR', label: '2. Config. Gestor Destino' },
                                                    { code: 'PF', label: '3. Aprovação Final' },
                                                    { code: 'AP', label: '4. Efetivado' },
                                                ].map((step, i) => {
                                                    const statusOrder = ['PC', 'PR', 'PF', 'AP'];
                                                    const currentIdx = statusOrder.indexOf(activeTransfer.status);
                                                    const stepIdx = statusOrder.indexOf(step.code);
                                                    const done = currentIdx > stepIdx || activeTransfer.status === 'AP';
                                                    const active = activeTransfer.status === step.code;

                                                    return (
                                                        <React.Fragment key={step.code}>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <div className={`rounded-circle d-flex align-items-center justify-content-center fw-bold`}
                                                                    style={{
                                                                        width: 28, height: 28, fontSize: 12, flexShrink: 0,
                                                                        background: done ? '#198754' : active ? '#ffc107' : '#e9ecef',
                                                                        color: done ? '#fff' : active ? '#212529' : '#6c757d',
                                                                    }}>
                                                                    {done ? <FontAwesomeIcon icon={solid('check')} size="xs" /> : i + 1}
                                                                </div>
                                                                <span className={`small ${active ? 'fw-semibold' : 'text-muted'}`}>{step.label}</span>
                                                            </div>
                                                            {i < 3 && <div style={{ width: 20, height: 2, background: done ? '#198754' : '#dee2e6' }} />}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Histórico */}
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-bottom">
                                        <span className="fw-semibold text-muted">
                                            <FontAwesomeIcon icon={solid('list')} className="me-2" />
                                            Histórico de Movimentações
                                        </span>
                                    </div>
                                    <div className="card-body p-0">
                                        {transfersLoad && transfers.length === 0 ? (
                                            <div className="text-center py-5 text-muted">
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Carregando...
                                            </div>
                                        ) : transfers.length === 0 ? (
                                            <div className="text-center py-5 text-muted">
                                                <FontAwesomeIcon icon={solid('arrow-right-arrow-left')} size="2x" className="mb-2 d-block mx-auto" />
                                                <p className="small">Nenhuma movimentação registrada.</p>
                                            </div>
                                        ) : (
                                            <ul className="list-group list-group-flush">
                                                {transfers.map(t => (
                                                    <li key={t.id} className="list-group-item px-4 py-3">
                                                        <div className="d-flex justify-content-between align-items-start gap-3">
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                    <StatusBadge status={t.status} />
                                                                    <span className="fw-semibold small">{t.reason_display}</span>
                                                                </div>
                                                                <div className="d-flex gap-3 flex-wrap">
                                                                    <span className="small text-muted">
                                                                        <FontAwesomeIcon icon={solid('calendar')} className="me-1" />
                                                                        Efetiva: {formatDate(t.effective_date)}
                                                                    </span>
                                                                    {t.future_job_position && (
                                                                        <span className="small text-muted">
                                                                            <FontAwesomeIcon icon={solid('briefcase')} className="me-1" />
                                                                            {t.future_job_position.title}
                                                                        </span>
                                                                    )}
                                                                    <span className="small text-muted">
                                                                        <FontAwesomeIcon icon={solid('user')} className="me-1" />
                                                                        Destino: {t.receiving_manager?.name || '—'}
                                                                    </span>
                                                                </div>
                                                                {t.notes && (
                                                                    <p className="small text-muted mb-0 mt-1">{t.notes}</p>
                                                                )}
                                                            </div>
                                                            <span className="small text-muted text-nowrap">{formatDate(t.created)}</span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {transfersNext && (
                                            <div className="text-center py-3">
                                                <button className="btn btn-outline-secondary btn-sm" disabled={transfersLoad} onClick={() => loadTransfers(transfersPage + 1)}>
                                                    {transfersLoad ? <span className="spinner-border spinner-border-sm" /> : 'Carregar mais'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Offcanvas: Iniciar Movimentação */}
            <div className="offcanvas offcanvas-end" tabIndex="-1" ref={offCanvasInitRef} style={{ width: 480 }}>
                <div className="offcanvas-header border-bottom">
                    <h6 className="offcanvas-title fw-bold">
                        <FontAwesomeIcon icon={solid('arrow-right-arrow-left')} className="me-2 text-green" />
                        Iniciar Movimentação
                    </h6>
                    <button type="button" className="btn-close" onClick={() => closeCanvas(offCanvasInitRef)} />
                </div>
                <div className="offcanvas-body">
                    <p className="text-muted small mb-3">
                        Você está iniciando a movimentação de <strong>{userEmployee?.user?.name}</strong> para sua equipe.
                        O gestor atual será notificado para revisar.
                    </p>
                    <form onSubmit={handleSubmitTransfer}>

                        <div className="mb-3">
                            <label className="form-label small fw-semibold">Posição de destino <span className="text-danger">*</span></label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Digite o nome da posição..."
                                    value={jobSearch}
                                    onChange={e => searchJobs(e.target.value)}
                                />
                                {jobSearching && (
                                    <span className="position-absolute top-50 end-0 translate-middle-y me-2">
                                        <span className="spinner-border spinner-border-sm text-muted" />
                                    </span>
                                )}
                            </div>
                            {jobResults.length > 0 && (
                                <ul className="list-group mt-1 shadow-sm" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {jobResults.map(j => (
                                        <li key={j.id}
                                            className="list-group-item list-group-item-action py-2 px-3 small"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => { setSelectedJob(j); setJobSearch(j.title); setJobResults([]); loadSuperiors(j.id); }}>
                                            <div className="fw-semibold">{j.title}</div>
                                            {j.internal_code && <span className="text-muted">{j.internal_code}</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {selectedJob && (
                                <div className="alert alert-success py-2 small mt-2 mb-0">
                                    <FontAwesomeIcon icon={solid('check-circle')} className="me-1" />
                                    <strong>{selectedJob.title}</strong>
                                    {selectedJob.internal_code && <span className="text-muted ms-1">({selectedJob.internal_code})</span>}
                                </div>
                            )}
                        </div>

                        {/* Superior imediato — determinado pela posição-pai da posição selecionada */}
                        <div className="mb-3">
                            <label className="form-label small fw-semibold">
                                Superior imediato
                                {superiorOptions.length > 1 && <span className="text-danger ms-1">*</span>}
                            </label>
                            {!selectedJob ? (
                                <p className="text-muted small fst-italic mb-0">Selecione a posição de destino</p>
                            ) : superiorsLoading ? (
                                <div className="d-flex align-items-center gap-2 text-muted small">
                                    <span className="spinner-border spinner-border-sm" />
                                    Buscando gestor da posição...
                                </div>
                            ) : superiorOptions.length === 0 ? (
                                <div className="alert alert-secondary py-2 small mb-0">
                                    <FontAwesomeIcon icon={solid('circle-info')} className="me-1" />
                                    Nenhum gestor definido para esta posição
                                </div>
                            ) : superiorOptions.length === 1 ? (
                                <div className="d-flex align-items-center gap-2 p-2 rounded border bg-light">
                                    <FontAwesomeIcon icon={solid('user-tie')} className="text-green" />
                                    <div className="flex-grow-1">
                                        <p className="fw-semibold small mb-0">{superiorOptions[0].name}</p>
                                        <p className="text-muted small mb-0">Gestor da posição de destino</p>
                                    </div>
                                    <FontAwesomeIcon icon={solid('circle-check')} className="text-success" size="sm" />
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-1">
                                    {superiorOptions.map(emp => (
                                        <div
                                            key={emp.id}
                                            className={`d-flex align-items-center gap-2 p-2 rounded border small ${selectedSuperior?.id === emp.id ? 'border-success bg-light' : ''}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setSelectedSuperior(emp)}
                                        >
                                            <input
                                                type="radio"
                                                readOnly
                                                checked={selectedSuperior?.id === emp.id}
                                                className="flex-shrink-0"
                                            />
                                            <FontAwesomeIcon icon={solid('user-tie')} className="text-muted" />
                                            <span className="fw-semibold flex-grow-1">{emp.name}</span>
                                            {selectedSuperior?.id === emp.id && (
                                                <FontAwesomeIcon icon={solid('circle-check')} className="text-success" size="sm" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label small fw-semibold">Motivo <span className="text-danger">*</span></label>
                            <select className="form-select form-select-sm" value={formReason} onChange={e => setFormReason(e.target.value)} required>
                                {REASON_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label className="form-label small fw-semibold">Data efetiva <span className="text-danger">*</span></label>
                            <input type="date" className="form-control form-control-sm" value={formDate} onChange={e => setFormDate(e.target.value)} required />
                        </div>
                        <div className="mb-4">
                            <label className="form-label small fw-semibold">Observações</label>
                            <textarea className="form-control form-control-sm" rows={2} value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Justifique a movimentação..." />
                        </div>
                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-success btn-sm btn-rounded px-3"
                                disabled={formSubmitting || !selectedJob || superiorsLoading || (superiorOptions.length > 0 && !selectedSuperior)}>
                                {formSubmitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                                Enviar solicitação
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm btn-rounded" onClick={() => closeCanvas(offCanvasInitRef)}>
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Offcanvas: Detalhe / Revisão / Aprovação Final */}
            <div className="offcanvas offcanvas-end" tabIndex="-1" ref={offCanvasDetailRef} style={{ width: 520 }}>
                <div className="offcanvas-header border-bottom">
                    <h6 className="offcanvas-title fw-bold">
                        <FontAwesomeIcon icon={solid('eye')} className="me-2 text-green" />
                        Detalhes da Movimentação
                    </h6>
                    <button type="button" className="btn-close" onClick={() => closeCanvas(offCanvasDetailRef)} />
                </div>
                <div className="offcanvas-body">
                    {selectedTransfer && (
                        <>
                            <div className="mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <StatusBadge status={selectedTransfer.status} />
                                    <span className="fw-semibold">{selectedTransfer.reason_display}</span>
                                </div>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <p className="small text-muted mb-0">Colaborador</p>
                                        <p className="fw-semibold small mb-0">{selectedTransfer.employee?.name}</p>
                                    </div>
                                    <div className="col-6">
                                        <p className="small text-muted mb-0">Data efetiva</p>
                                        <p className="fw-semibold small mb-0">{formatDate(selectedTransfer.effective_date)}</p>
                                    </div>
                                    <div className="col-6">
                                        <p className="small text-muted mb-0">Gestor atual</p>
                                        <p className="fw-semibold small mb-0">{selectedTransfer.current_manager?.name || '—'}</p>
                                    </div>
                                    <div className="col-6">
                                        <p className="small text-muted mb-0">Gestor de destino</p>
                                        <p className="fw-semibold small mb-0">{selectedTransfer.receiving_manager?.name || '—'}</p>
                                    </div>
                                    {selectedTransfer.future_job_position && (
                                        <div className="col-12">
                                            <p className="small text-muted mb-0">Posição de destino</p>
                                            <p className="fw-semibold small mb-0">{selectedTransfer.future_job_position.title}</p>
                                            <p className="small text-muted mb-0">{selectedTransfer.future_job_position.subcompany?.title} · {selectedTransfer.future_job_position.department?.title}</p>
                                        </div>
                                    )}
                                    {selectedTransfer.notes && (
                                        <div className="col-12">
                                            <p className="small text-muted mb-0">Observações (destino)</p>
                                            <p className="small mb-0">{selectedTransfer.notes}</p>
                                        </div>
                                    )}
                                    {selectedTransfer.current_manager_notes && (
                                        <div className="col-12">
                                            <p className="small text-muted mb-0">Observações (gestor atual)</p>
                                            <p className="small mb-0">{selectedTransfer.current_manager_notes}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Revisão do gestor atual */}
                            {isCurrentManager && selectedTransfer.status === 'PC' && (
                                <div className="border-top pt-3">
                                    <h6 className="fw-semibold mb-3">Sua revisão</h6>
                                    <div className="mb-3">
                                        <label className="form-label small fw-semibold">Observações (opcional)</label>
                                        <textarea className="form-control form-control-sm" rows={3} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Adicione comentários sobre a movimentação..." />
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-success btn-sm btn-rounded px-3" disabled={reviewSubmitting} onClick={() => handleReview('approve')}>
                                            {reviewSubmitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                                            <FontAwesomeIcon icon={solid('check')} className="me-1" />
                                            Aprovar
                                        </button>
                                        <button className="btn btn-outline-danger btn-sm btn-rounded" disabled={reviewSubmitting} onClick={() => handleReview('reject')}>
                                            <FontAwesomeIcon icon={solid('xmark')} className="me-1" />
                                            Rejeitar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Aprovação final */}
                            {isFinalApprover && selectedTransfer.status === 'PF' && (
                                <div className="border-top pt-3">
                                    <h6 className="fw-semibold mb-3">Aprovação Final</h6>
                                    <p className="small text-muted mb-3">
                                        Ao aprovar, o sistema irá efetivar a movimentação: atualizar o gestor e a posição do colaborador automaticamente.
                                    </p>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-success btn-sm btn-rounded px-3" disabled={finalSubmitting} onClick={() => handleFinalApproval('approve')}>
                                            {finalSubmitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                                            <FontAwesomeIcon icon={solid('stamp')} className="me-1" />
                                            Aprovar e Efetivar
                                        </button>
                                        <button className="btn btn-outline-danger btn-sm btn-rounded" disabled={finalSubmitting} onClick={() => handleFinalApproval('reject')}>
                                            <FontAwesomeIcon icon={solid('xmark')} className="me-1" />
                                            Rejeitar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Offcanvas: Configurar posição (Gestor Destino - Etapa 3) */}
            <div className="offcanvas offcanvas-end" tabIndex="-1" ref={offCanvasConfigureRef} style={{ width: 520 }}>
                <div className="offcanvas-header border-bottom">
                    <h6 className="offcanvas-title fw-bold">
                        <FontAwesomeIcon icon={solid('sliders')} className="me-2 text-info" />
                        Confirmar Movimentação
                    </h6>
                    <button type="button" className="btn-close" onClick={() => closeCanvas(offCanvasConfigureRef)} />
                </div>
                <div className="offcanvas-body">
                    {selectedTransfer && (
                        <>
                            <p className="text-muted small mb-3">
                                Confirme os dados da movimentação de <strong>{selectedTransfer.employee?.name}</strong> antes de enviar para aprovação final.
                            </p>

                            {/* Superior imediato — fixo: o próprio requisitante */}
                            <div className="mb-4">
                                <label className="form-label small fw-semibold">Superior imediato</label>
                                <div className="d-flex align-items-center gap-2 p-2 rounded border bg-light">
                                    <FontAwesomeIcon icon={solid('user-tie')} className="text-green" />
                                    <div>
                                        <p className="fw-semibold small mb-0">{selectedTransfer.receiving_manager?.name}</p>
                                        <p className="text-muted small mb-0">Gestor de destino (você)</p>
                                    </div>
                                    <FontAwesomeIcon icon={solid('lock')} className="text-muted ms-auto" size="xs" />
                                </div>
                            </div>

                            <form onSubmit={handleConfigure}>
                                <div className="mb-4">
                                    <label className="form-label small fw-semibold">Posição de destino</label>
                                    {selectedJob ? (
                                        <div className="d-flex align-items-start gap-2 p-3 rounded border bg-light">
                                            <FontAwesomeIcon icon={solid('briefcase')} className="text-green mt-1 flex-shrink-0" />
                                            <div className="flex-grow-1">
                                                <p className="fw-semibold small mb-1">{selectedJob.title}</p>
                                                {selectedJob.internal_code && (
                                                    <p className="text-muted small mb-1">Código: {selectedJob.internal_code}</p>
                                                )}
                                                {selectedJob.subcompany && (
                                                    <p className="text-muted small mb-0">{selectedJob.subcompany?.title || selectedJob.subcompany}</p>
                                                )}
                                            </div>
                                            <FontAwesomeIcon icon={solid('lock')} className="text-muted flex-shrink-0" size="xs" />
                                        </div>
                                    ) : (
                                        <div className="alert alert-warning py-2 small mb-0">
                                            <FontAwesomeIcon icon={solid('triangle-exclamation')} className="me-1" />
                                            Posição não definida na solicitação
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex gap-2">
                                    <button type="submit" className="btn btn-info btn-sm btn-rounded px-3 text-white" disabled={configSubmitting || !selectedJob}>
                                        {configSubmitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                                        Confirmar e Enviar
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary btn-sm btn-rounded" onClick={() => closeCanvas(offCanvasConfigureRef)}>
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>

        </ProtectedRoute>
    );
}
